import { useCallback, useEffect, useState } from 'react';
// import { useParams } from 'react-router-dom';

import { useAppSelector } from './useAppSelector';
import { useSocket } from './useSocket';
import { fetchNotificationUnreadCount } from '@/store/slices/notifications/notifications-api';
import type { NotificationItem } from '@/types/notification';
import { shouldEnableNotifications } from '@/utils/role';

/**
 * Unread notification badge count: REST on connect + `notification.created` over Socket.IO.
 * Uses {@link useSocket} so additional notification features can share the same connection.
 */
const useNotificationUnreadCount = () => {
    //const { hospitalId: hospitalIdFromRoute } = useParams<{ hospitalId?: string }>();
    // const { currentHospitalId } = useAppSelector(({ hospital }) => hospital);
    const isLoggedIn = useAppSelector(({ auth }) => auth.isLoggedIn);
    const { profile } = useAppSelector(({ profile: profileState }) => profileState);
    const notificationsEnabled = shouldEnableNotifications(profile);
    // const hospitalId = hospitalIdFromRoute ?? currentHospitalId ?? undefined;
    const { socket, isConnected, connectionStatus } = useSocket({ enabled: notificationsEnabled });
    const [unreadCount, setUnreadCount] = useState(0);

    const refreshUnreadCount = useCallback(async () => {
        if (!isLoggedIn || !notificationsEnabled) {
            setUnreadCount(0);
            return;
        }

        try {
            const { count } = await fetchNotificationUnreadCount();
            setUnreadCount(count);
        } catch {
            // Keep last known count when the API is temporarily unavailable
        }
    }, [isLoggedIn, notificationsEnabled]);

    useEffect(() => {
        if (!isConnected || !notificationsEnabled) {
            return;
        }
        refreshUnreadCount();
    }, [isConnected, notificationsEnabled, refreshUnreadCount]);

    useEffect(() => {
        if (!notificationsEnabled || !socket) {
            return undefined;
        }

        const onCreated = (_payload: NotificationItem) => {
            setUnreadCount((count) => count + 1);
        };

        socket.on('notification.created', onCreated);

        return () => {
            socket.off('notification.created', onCreated);
        };
    }, [notificationsEnabled, socket]);

    return {
        unreadCount,
        refreshUnreadCount,
        isConnected,
        connectionStatus
    };
};

export { useNotificationUnreadCount };
